import { jsx as _jsx } from "react/jsx-runtime";
import { useState, useEffect, useContext, useCallback, createContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setTheme } from './theme-slice';
// Only keeping the five themes as specified: light, dark, mellowYellow, purpleRain, blueSky
const themes = {
    light: {
        name: 'light',
        colors: {
            background: '#FAFAFA',
            text: '#171717',
            primary: '#2563EB',
            secondary: '#F5A623',
            error: '#DC2626',
        },
    },
    dark: {
        name: 'dark',
        colors: {
            background: '#1A2332',
            text: '#F5F7FA',
            primary: '#3B82F6',
            secondary: '#FFC107',
            error: '#FF6B6B',
        },
    },
    mellowYellow: {
        name: 'mellowYellow',
        colors: {
            background: '#FFF5CC',
            text: '#4D2600',
            primary: '#E6AD00',
            secondary: '#FF9800',
            error: '#E64A19',
        },
    },
    purpleRain: {
        name: 'purpleRain',
        colors: {
            background: '#E6CCF2',
            text: '#330066',
            primary: '#400080',
            secondary: '#9C27B0',
            error: '#DC2626',
        },
    },
    blueSky: {
        name: 'blueSky',
        colors: {
            background: '#CCE5FF',
            text: '#003366',
            primary: '#0099FF',
            secondary: '#1A8CFF',
            error: '#FF4D4D',
        },
    },
};
const ThemeContext = createContext(undefined);
export const useTheme = () => {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
};
// Helper function to get the system theme preference
const getSystemTheme = () => {
    if (typeof window !== 'undefined' && window.matchMedia) {
        return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    return 'light';
};
export function ThemeProvider({ children }) {
    const dispatch = useDispatch();
    const currentThemeMode = useSelector((state) => state.theme.current);
    // Resolve 'system' theme to actual system preference
    const resolvedThemeMode = currentThemeMode === 'system' ? getSystemTheme() : currentThemeMode;
    const [currentTheme, setCurrentTheme] = useState(themes[resolvedThemeMode] || themes.light);
    useEffect(() => {
        // Resolve 'system' theme to actual system preference
        const resolvedMode = currentThemeMode === 'system' ? getSystemTheme() : currentThemeMode;
        // Safely set the current theme, defaulting to light if the theme doesn't exist
        if (themes[resolvedMode]) {
            setCurrentTheme(themes[resolvedMode]);
        }
        else {
            console.warn(`Theme "${currentThemeMode}" not found, using light theme as fallback`);
            setCurrentTheme(themes.light);
        }
        // Set up listener for system theme changes when theme is set to 'system'
        if (currentThemeMode === 'system' && typeof window !== 'undefined' && window.matchMedia) {
            const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
            const handleSystemThemeChange = (e) => {
                const newSystemTheme = e.matches ? 'dark' : 'light';
                if (themes[newSystemTheme]) {
                    setCurrentTheme(themes[newSystemTheme]);
                }
            };
            // Use addEventListener if available (modern browsers)
            if (mediaQuery.addEventListener) {
                const handler = (e) => {
                    const newSystemTheme = e.matches ? 'dark' : 'light';
                    if (themes[newSystemTheme]) {
                        setCurrentTheme(themes[newSystemTheme]);
                    }
                };
                mediaQuery.addEventListener('change', handler);
                return () => {
                    mediaQuery.removeEventListener('change', handler);
                };
            }
            else {
                // Fallback for older browsers (addListener passes MediaQueryList directly)
                // @ts-ignore - addListener is deprecated but needed for older browser support
                const handler = (e) => {
                    const newSystemTheme = e.matches ? 'dark' : 'light';
                    if (themes[newSystemTheme]) {
                        setCurrentTheme(themes[newSystemTheme]);
                    }
                };
                // @ts-ignore - addListener is deprecated but needed for older browser support
                mediaQuery.addListener(handler);
                return () => {
                    // @ts-ignore - removeListener is deprecated but needed for older browser support
                    mediaQuery.removeListener(handler);
                };
            }
        }
    }, [currentThemeMode]);
    const toggleTheme = useCallback((themeName) => {
        // Allow 'system' theme or any theme that exists in the themes object
        if (themeName === 'system' || themes[themeName]) {
            dispatch(setTheme(themeName));
        }
        else {
            console.warn(`Cannot set theme "${themeName}" as it doesn't exist`);
        }
    }, [dispatch]);
    return (_jsx(ThemeContext.Provider, { value: { theme: currentTheme, toggleTheme }, children: children }));
}
;
export default ThemeProvider;
